import { TopBar } from '@openx/components-table/ChipFilters/TopBar';
import { Table, TableName } from '@openx/components-table/Table';
import type { OrganizationTreeRowData } from '@openx/types/organization';
import { type AllTableCriteria, type CriteriaChange, CriteriaDimension } from '@openx/types/tableCriteria';
import { sortMenuButtonItems } from '@openx/utils/lib/tableHelpers';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganizationsListFilters } from '../../types';
import { useOrganizationContext } from '../../utils/context/OrganizationContext';

type OrganizationsListProps = {
  criteria: AllTableCriteria<OrganizationsListFilters>;
  handleCriteriaChange: (newCriteria: CriteriaChange<CriteriaDimension, OrganizationsListFilters>) => void;
  loading: boolean;
  organizations: OrganizationTreeRowData[];
};

export const OrganizationsList = ({
  criteria,
  handleCriteriaChange,
  loading,
  organizations,
}: OrganizationsListProps) => {
  const { t } = useTranslation();
  const { useGetOrganizationsTableData } = useOrganizationContext();
  const { rowActions, tableColumns } = useGetOrganizationsTableData({
    organizations,
    searchPhrase: criteria.filters?.phrase,
  });

  const handleTopBarFiltersChange = useCallback(
    (newFilters: OrganizationsListFilters) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value: newFilters,
      });
    },
    [handleCriteriaChange]
  );

  const isSearchActive = Boolean(criteria.filters?.phrase);

  const setIsForcedExpanded = useCallback(
    (organization: OrganizationTreeRowData) => {
      return organization.depth === 0 && Boolean(organization.children?.length) && !isSearchActive;
    },
    [isSearchActive]
  );

  const tableRowActions = sortMenuButtonItems(rowActions, [rowActions.CREATE]);

  return (
    <>
      <TopBar
        filters={criteria.filters as OrganizationsListFilters}
        onChange={handleTopBarFiltersChange}
        searchText={t('Search Name')}
      />

      <Table
        // Need a key to remount the rows after search criteria change, otherwise cached results render
        key={isSearchActive ? 'search' : 'tree'}
        name={TableName.ORGANIZATIONS}
        columns={tableColumns}
        criteria={criteria}
        data={organizations}
        loading={loading}
        onCriteriaChange={handleCriteriaChange}
        rowActions={tableRowActions}
        primaryRowAction={rowActions.CREATE}
        isTree={true}
        isForcedExpanded={setIsForcedExpanded}
      />
    </>
  );
};
